import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import models.BankoGameEngine
import models.DomainGame
import models.FirebaseGame
import models.GameDataSource
import models.GameSettings
import models.GinkoEvent
import models.Nudge
import models.Player
import models.JoinGameResult
import models.PublicGameSummary
import models.PUBLIC_GAME_MAX_PLAYERS
import models.Round
import models.RoundModifier
import models.Setting
import models.TableReaction
import models.GameRecapStats
import models.afterBank
import models.afterRoll
import models.heistBonusesFor

class LocalGameDataSource : GameDataSource {
    private val games = mutableMapOf<String, MutableStateFlow<FirebaseGame?>>()
    private val players = mutableMapOf<String, MutableStateFlow<List<Player>>>()
    private val reactions = mutableMapOf<String, MutableStateFlow<List<TableReaction>>>()

    override suspend fun deleteGame(gameCode: String) {
        // Null the flow before dropping it so anything still collecting sees the game end.
        games[gameCode]?.value = null
        games.remove(gameCode)
        players.remove(gameCode)
        reactions.remove(gameCode)
    }

    override suspend fun finishGame(gameCode: String) {
        // No-op for local games
    }

    override suspend fun updateSetting(gameCode: String, game: DomainGame, settingToUpdate: Setting) {
        games[gameCode]?.value?.settings?.let { settings ->
            val index = settings.indexOfFirst { it.name == settingToUpdate.name }
            val updatedSettings = if (index != -1) {
                settings.toMutableList().apply { this[index] = settingToUpdate }
            } else {
                settings + settingToUpdate
            }
            games[gameCode]?.value = games[gameCode]?.value?.copy(settings = updatedSettings)
        }
    }

    override suspend fun makeHost(gameCode: String, playerName: String) {
        val ownerUid = players[gameCode]?.value?.firstOrNull { it.name == playerName }?.ownerUid.orEmpty()
        games[gameCode]?.value = games[gameCode]?.value?.copy(host = playerName, hostUid = ownerUid)
    }

    override suspend fun removePlayer(gameCode: String, playerName: String) {
        val game = games[gameCode]?.value ?: return
        val currentPlayers = players[gameCode]?.value ?: emptyList()
        val domainGame = mapToDomainGame(game, currentPlayers) ?: return
        val removalDecision = BankoGameEngine.removePlayer(domainGame, playerName)
        val removedUid = currentPlayers.firstOrNull { it.name == playerName }?.ownerUid.orEmpty()
        val remainingPlayers = currentPlayers.filterNot { it.name == playerName }
        val remainingHasRemovedUid = removedUid.isNotBlank() && remainingPlayers.any { it.ownerUid == removedUid }
        val nextHost = if (game.host == playerName) {
            removalDecision.orderedPlayerNames
                .mapNotNull { name -> remainingPlayers.firstOrNull { it.name == name } }
                .firstOrNull { !it.hostCreated && !it.isBot }
                ?.name
                ?: removalDecision.orderedPlayerNames.firstOrNull().orEmpty()
        } else {
            game.host
        }
        val nextHostUid = remainingPlayers.firstOrNull { it.name == nextHost }?.ownerUid.orEmpty()

        players[gameCode]?.value = remainingPlayers
        games[gameCode]?.value = game.copy(
            orderedPlayerNames = removalDecision.orderedPlayerNames,
            round = removalDecision.round,
            currentPlayer = removalDecision.currentPlayer,
            host = nextHost,
            hostUid = nextHostUid,
            participantUids = if (removedUid.isNotBlank() && !remainingHasRemovedUid) {
                game.participantUids.filterNot { it == removedUid }
            } else {
                game.participantUids
            },
            // Match the remote path: a finishing removal drops endRoundNum so the game-over
            // screen shows, consistent with a normal end.
            endRoundNum = if (removalDecision.shouldFinishGame) -1 else game.endRoundNum,
        )
    }

    override suspend fun bankPoints(
        gameCode: String,
        bankingPlayers: List<String>,
        nextRoundModifier: RoundModifier,
    ) {
        val game = games[gameCode]?.value ?: return
        val round = game.round ?: return
        val domainGame = mapToDomainGame(game, players[gameCode]?.value ?: emptyList()) ?: return
        val bankDecision = BankoGameEngine.bank(domainGame, domainGame.currentPlayer, bankingPlayers) ?: return
        val activeGinkoEvent = round.ginkoEvent
        val remainingPlayers = bankDecision.remainingPlayers
        val skimByRider = if (activeGinkoEvent == GinkoEvent.HEIST_WINDOW) {
            remainingPlayers.associateWith { rider ->
                minOf(
                    GinkoEvent.HEIST_SKIM_POINTS,
                    players[gameCode]?.value?.firstOrNull { it.name == rider }?.points ?: 0,
                )
            }
        } else {
            emptyMap()
        }
        val heistBonuses = heistBonusesFor(bankDecision.bankingPlayers, skimByRider.values.sum())
        val updatedRecapStats = game.recapStats.afterBank(
            round = round,
            bankingPlayerNames = bankDecision.bankingPlayers,
            heistBonuses = heistBonuses,
            heistSkims = skimByRider,
            roundEnded = remainingPlayers.isEmpty(),
        )

        bankDecision.bankingPlayers.forEach { playerName ->
            val heistBonus = heistBonuses[playerName] ?: 0
            players[gameCode]?.value = players[gameCode]?.value?.map { player ->
                if (player.name == playerName) {
                    player.copy(points = player.points + round.currentPoints + heistBonus)
                } else {
                    player
                }
            } ?: emptyList()
        }
        skimByRider.forEach { (riderName, skim) ->
            if (skim > 0) {
                players[gameCode]?.value = players[gameCode]?.value?.map { player ->
                    if (player.name == riderName) {
                        player.copy(points = (player.points - skim).coerceAtLeast(0))
                    } else {
                        player
                    }
                } ?: emptyList()
            }
        }
        if (remainingPlayers.isEmpty()) {
            // All players have banked, advance round under the next modifier (Jackpot seeds the pot).
            val newRoundNum = round.roundNum + 1
            val newRound = Round(
                roundNum = newRoundNum,
                currentPoints = nextRoundModifier.startingPoints(),
                dieOne = 0,
                dieTwo = 0,
                currentRoll = 1,
                activeOrderedPlayerNames = domainGame.orderedPlayerNames,
                modifier = nextRoundModifier,
                ginkoEvent = GinkoEvent.NONE,
                ginkoEventsTriggered = 0,
                bankLockoutTurnsRemaining = 0,
            )
            games[gameCode]?.value = game.copy(
                round = newRound,
                currentPlayer = bankDecision.nextPlayerName,
                recapStats = updatedRecapStats,
            )
        } else {
            // Not all players have banked, just update activeOrderedPlayerNames and currentPlayer
            games[gameCode]?.value = game.copy(
                round = round.copy(
                    activeOrderedPlayerNames = remainingPlayers,
                    ginkoEvent = activeGinkoEvent.afterBank(),
                ),
                currentPlayer = bankDecision.nextPlayerName,
                recapStats = updatedRecapStats,
            )
        }
    }

    override suspend fun rollDice(
        gameCode: String,
        expectedRound: Round?,
        round: Round,
        nextPlayerName: String,
        rolledValue: Int,
    ) {
        val game = games[gameCode]?.value ?: return
        if (game.round != expectedRound || game.currentPlayer != round.rolledBy) return
        val previousRound = game.round ?: return
        games[gameCode]?.value = game.copy(
            round = round,
            currentPlayer = nextPlayerName,
            recapStats = game.recapStats.afterRoll(
                previousRound = previousRound,
                updatedRound = round,
                rollerName = round.rolledBy,
                rolledValue = rolledValue,
            ),
        )
    }

    override suspend fun startGame(
        gameCode: String,
        activeOrderedPlayerNames: List<String>,
        firstRoundModifier: RoundModifier,
    ) {
        games[gameCode]?.value = games[gameCode]?.value?.copy(
            round = Round(
                roundNum = 1,
                currentPoints = firstRoundModifier.startingPoints(),
                dieOne = 0,
                dieTwo = 0,
                currentRoll = 1,
                activeOrderedPlayerNames = activeOrderedPlayerNames,
                modifier = firstRoundModifier,
            ),
            isPublic = false,
            recapStats = GameRecapStats(),
        )
    }

    override suspend fun createGame(gameCode: String, name: String, hostPerksActive: Boolean, diceSkin: String, diceHat: String): String {
        val player = Player(name = name, points = 0, hostCreated = true, diceSkin = diceSkin, diceHat = diceHat, ownerUid = "LOCAL")
        val game = FirebaseGame(
            dateLastModified = Timestamp.now(),
            joinCode = gameCode,
            endRoundNum = 10,
            orderedPlayerNames = listOf(name),
            host = name,
            hostUid = "LOCAL",
            currentPlayer = name,
            settings = GameSettings.defaultSettings,
            hostPerksActive = hostPerksActive,
            participantUids = listOf("LOCAL"),
        )
        games[gameCode] = MutableStateFlow(game)
        players[gameCode] = MutableStateFlow(listOf(player))
        return gameCode
    }

    override suspend fun rematch(gameCode: String, hostPerksActive: Boolean) {
        val game = games[gameCode]?.value ?: return
        players[gameCode]?.value = players[gameCode]?.value?.map { it.copy(points = 0) } ?: emptyList()
        games[gameCode]?.value = game.copy(
            round = null,
            finished = false,
            currentPlayer = game.host,
            nudge = null,
            hostPerksActive = hostPerksActive,
            recapStats = GameRecapStats(),
            // A finishing player-removal forces game-over by dropping endRoundNum below 1;
            // restore the default so the rematch lobby isn't instantly over.
            endRoundNum = if (game.endRoundNum < 1) 10 else game.endRoundNum,
        )
    }

    override suspend fun joinGame(player: Player, gameCode: String): JoinGameResult {
        val game = games[gameCode]?.value ?: return JoinGameResult.NOT_FOUND
        if (game.round != null || game.finished) return JoinGameResult.ALREADY_STARTED
        if (players[gameCode]?.value?.any { it.name.equals(player.name, ignoreCase = true) } == true) {
            return JoinGameResult.NAME_TAKEN
        }
        if (game.isPublic && game.orderedPlayerNames.size >= PUBLIC_GAME_MAX_PLAYERS) {
            return JoinGameResult.FULL
        }
        addPlayer(player.copy(ownerUid = player.ownerUid.ifBlank { player.name }), gameCode)
        return JoinGameResult.JOINED
    }

    override suspend fun setGamePublic(gameCode: String, isPublic: Boolean) {
        games[gameCode]?.value = games[gameCode]?.value?.copy(isPublic = isPublic)
    }

    override fun publicGamesFlow(): Flow<List<PublicGameSummary>> = MutableStateFlow(emptyList())

    override fun firebaseGameFlow(gameCode: String): Flow<FirebaseGame?> =
        games[gameCode] ?: MutableStateFlow(null)

    override fun firebasePlayersFlow(gameCode: String): Flow<List<Player>> =
        players[gameCode] ?: MutableStateFlow(emptyList())

    override fun tableReactionsFlow(gameCode: String): Flow<List<TableReaction>> =
        reactions.getOrPut(gameCode) { MutableStateFlow(emptyList()) }

    override fun domainGameFlow(gameCode: String): Flow<DomainGame?> =
        combine(firebaseGameFlow(gameCode), firebasePlayersFlow(gameCode)) { firebaseGame, players ->
            mapToDomainGame(firebaseGame, players)
        }

    override suspend fun gameExists(gameCode: String): Boolean =
        games.containsKey(gameCode)

    override suspend fun updatePlayerOrder(gameCode: String, playerNames: List<String>) {
        games[gameCode]?.value = games[gameCode]?.value?.copy(orderedPlayerNames = playerNames)
    }

    override suspend fun updateEndRoundNum(gameCode: String, endRoundNum: Int) {
        games[gameCode]?.value = games[gameCode]?.value?.copy(endRoundNum = endRoundNum)
    }

    override suspend fun addPlayer(player: Player, gameCode: String) {
        val validation = PlayerNameValidator.validate(player.name)
        if (validation !is PlayerNameValidator.Result.Valid) return
        val validatedPlayer = player.copy(name = validation.name)
        if (players[gameCode]?.value?.any { it.name.equals(validatedPlayer.name, ignoreCase = true) } == true) return
        players[gameCode]?.value = players[gameCode]?.value?.plus(validatedPlayer) ?: listOf(validatedPlayer)
        games[gameCode]?.value = games[gameCode]?.value?.copy(
            orderedPlayerNames = games[gameCode]?.value?.orderedPlayerNames?.plus(validatedPlayer.name) ?: listOf(validatedPlayer.name),
            participantUids = (games[gameCode]?.value?.participantUids.orEmpty() + validatedPlayer.ownerUid).distinct(),
        )
    }

    // Local games are single-device, so there's no other device to buzz — nudging is a no-op.
    override suspend fun sendNudge(gameCode: String, nudge: Nudge) {
    }

    override suspend fun sendTableReaction(gameCode: String, reaction: TableReaction) {
        val reactionFlow = reactions.getOrPut(gameCode) { MutableStateFlow(emptyList()) }
        val senderKey = reaction.ownerUid.ifBlank { reaction.senderPlayer }
        reactionFlow.value = reactionFlow.value.filterNot {
            it.ownerUid.ifBlank { it.senderPlayer } == senderKey
        } + reaction
    }
}

private fun mapToDomainGame(firebaseGame: FirebaseGame?, players: List<Player>): DomainGame? {
    if (firebaseGame == null) {
        return null
    }
    return DomainGame(
        dateLastModified = firebaseGame.dateLastModified.toString(),
        joinCode = firebaseGame.joinCode,
        players = players,
        round = firebaseGame.round,
        endRoundNum = firebaseGame.endRoundNum,
        orderedPlayerNames = firebaseGame.orderedPlayerNames,
        host = firebaseGame.host,
        hostUid = firebaseGame.hostUid,
        currentPlayer = firebaseGame.currentPlayer,
        settings = firebaseGame.settings,
        finished = firebaseGame.finished,
        nudge = firebaseGame.nudge,
        hostPerksActive = firebaseGame.hostPerksActive,
        isPublic = firebaseGame.isPublic,
        publicSinceSeconds = firebaseGame.publicSince?.seconds,
        participantUids = firebaseGame.participantUids,
        recapStats = firebaseGame.recapStats,
    )
}
